import React, { Component } from 'react';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import PropTypes, { array, bool, func, object, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import * as LDClient from 'launchdarkly-js-client-sdk';
import IdleTimeWatcher, { resetActionTimer, resetIdleTimer } from 'ps-components/Utils/IdleTimeWatcher';
import { getSession, resetError, validateSession } from '../actions/session';
import { clearEmployeeFilter } from '../components/MarketPricing/LandingPage/LandingPageActions';
import { clearAllSelections } from '../components/MarketPricing/LandingPage/LandingPageActions';
import CompensationSummaryPdf from '../components/CompensationSummary/CompensationSummaryPdf/CompensationSummaryPdf';
import AddJobWrapper from '../containers/AddJob/AddJobWrapper';
import Home from './Home/Home';
import {
  DataCutMigrationDetailsPage,
  DataCutMigrationPage,
  GlobalJobPricingVersioningLanding,
  GlobalJobMatchingVersionsList,
  EditPricingVersionDetailsPage,
  GlobalJobMatchingPricingVersionStepper,
  GeoDifferentialPage,
  JobBasedRanges,
  JobMatching,
  MarketPricing,
  PayScalePulsePage,
  ReportLibrary,
  Report,
  RulesBasedPricing,
  StructureModelingPage,
  SurveyLibraryUpdate,
  ReportLibraryNew,
  BulkPricing,
  PropagateMatches
} from '../components/Authorization/ComponentsWithAuthorization';
import DefaultLayout from '../components/Layouts/DefaultLayout';
import PrintLayout from '../components/Layouts/PrintLayout';
import GlobalJobMatching from '../components/GlobalJobMatching/GlobalJobMatchingContainer';
import YearOverYearContainer from '../components/YearOverYear/YearOverYearContainer';
import NoMatch from '../components/Errors/404';
import SessionError from '../components/SessionError';
import StatusError from '../components/StatusError/StatusError';
import SFTPSetup from '../components/SFTPSetup/SFTPSetup';
import GoogleTagManager from 'ps-components/ThirdPartyWrappers/GoogleTagManager';
import SentryLogging from 'ps-components/ThirdPartyWrappers/SentryLogging';
import InPlaceIndicator from 'ps-components/LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import ModalRoot from './Modal/ModalRoot';
import { setPendoDarklyFlags, setPendoFeatureFlags } from 'ps-components/lib/formatters.js';
import transformFeatureFlags from '../lib/featureFlags.js';
import './App.scss';
import isEqual from 'lodash/isEqual';
import MeritIframeRoutes, { IFRAME_PATH } from '../components/Routing/Routes';
import { showSlideout } from 'ps-components/Slideout/SlideoutReducer';
import { clearReportFilters } from 'ps-components/Modules/ReportLibrary/ReportActions';
import LandingPage from '../components/MarketPricing/LandingPage/LandingPage';
import LandingDashboardPage from '../components/LandingDashboardPage/LandingDashboardPage';
import JDXIframe from '../components/HRTMS/JDXIframe';
import StructureModelingAnalyticsSlideout from '../components/StructureModeling/StructureModelAnalytics/StructureModelingAnalyticsSlideout';
import AlertToastContainer from 'ps-components/AlertToast/AlertToastContainer';
import { isProduction } from '../lib/helpers';
import SuperAdmin from './MarketPayInternal/MarketPayInternal';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { MantineProvider } from '@mantine/core';
import { theme } from '../lib/mantineTheme.tsx';

const BASIC_PRODUCT_NAME = 'MarketPay';

const PrintLayoutRoute = ({ component: Component, ...rest }) => {
  return (
    <Route
      {...rest}
      render={props => (
        <PrintLayout>
          <Component {...props} {...rest} />
        </PrintLayout>
      )}
    />
  );
};

export const DefaultLayoutRoute = ({ component: Component, layoutProps, componentProps, ...rest }) => {
  return (
    <Route
      {...rest}
      render={props => (
        <DefaultLayout {...layoutProps}>
          <Component {...props} {...componentProps} />
          <GoogleTagManager appLoadFinalProps={layoutProps.appLoadFinalProps} isLab={false} />
          <SentryLogging />
        </DefaultLayout>
      )}
    />
  );
};

const ModalLayoutRoute = ({ layoutProps, componentProps, ...rest }) => {
  return <Route {...rest} render={props => <ModalRoot history={layoutProps.history} {...componentProps} />} />;
};

class App extends Component {
  static propTypes = {
    dispatch: func,
    config: shape({
      openACSHost: string.isRequired
    }),
    session: shape({
      error: bool,
      loading: bool,
      loggedIn: bool,
      currency: object.isRequired,
      organization: object.isRequired,
      replacementTokens: array
    }),
    group: object,
    currency: object,
    getSession: func,
    clearEmployeeFilter: func,
    validateSession: func,
    location: object,
    history: PropTypes.object,
    showSlideout: func
  };

  constructor(props) {
    super(props);
    let navCollapsed = window.localStorage.getItem('navcollapsed');

    if (navCollapsed === null) {
      // when not set in localStorage, default to section closed
      navCollapsed = true;
    } else {
      // localStorage saves a string
      navCollapsed = navCollapsed === 'true';
    }

    this.state = {
      launchDarklyFeatureFlags: {},
      appLoadFinalProps: false,
      navCollapsed
    };
    this.previousLocation = this.props.location;

    this.getSessionData = this.getSessionData.bind(this);
  }

  toggleCollapse = event => {
    event.preventDefault();
    const navCollapsed = !this.state.navCollapsed;

    this.setState({ navCollapsed });
    window.localStorage.setItem('navcollapsed', navCollapsed);
  };

  componentDidMount() {
    this.getSessionData();
  }


  componentDidUpdate(prevProps) {
    const { group, session, location, history, reports } = this.props;
    const {
      includeFilter,
      scopeFilter,
      typeFilter,
      textFilter,
      collectionsFilter,
      collectionTypeFilter,
      sortDirection,
      sortField
    } = reports;

    if (
      history.action !== 'POP' &&
      (!prevProps.location.state || !prevProps.location.state.modal) &&
      prevProps.location.pathname !== location.pathname
    ) {
      this.previousLocation = prevProps.location;
    }

    if (
      group.groupId !== prevProps.group.groupId &&
      (prevProps.group.groupId || (!prevProps.group.groupId && group.groupId))
    ) {
      this.getSessionData(true);
      this.props.clearAllSelections();
      window.localStorage.removeItem('selectedJobs');
      this.props.history.replace({
        ...history.location,
        state: {
          ...history.location.state,
          jobID: null
        }
      });
    } else if (
      session.currency.id !== prevProps.session.currency.id &&
      (prevProps.session.currency.id || (!prevProps.session.currency.id && session.currency.id))
    ) {
      this.getSessionData();
    }

    if (session.user.id && session.user.id !== prevProps.session.user.id) {
      this.initLaunchDarklyFeatureFlags();
    }

    if (
      !location.pathname.includes('#reportWriter') &&
      prevProps.location.pathname.includes('reports/library') &&
      !location.pathname.includes('reports/library') &&
      (includeFilter ||
        scopeFilter ||
        typeFilter ||
        textFilter ||
        collectionsFilter ||
        collectionTypeFilter ||
        sortField !== 'name' ||
        sortDirection !== 'ASC')
    ) {
      this.props.clearReportFilters();
    }

    if (!this.props.location.pathname.includes('/market-analysis/market-pricing') &&
        !this.props.location.pathname.includes('/market-analysis/jobs') &&
        !this.props.location.pathname.includes('/market-analysis/multi-job-pricing') &&
        !this.props.location.pathname.includes('/market-analysis/propagate-matches')
       ) {
      this.props.clearEmployeeFilter()
    }

    if (!window.location.pathname.endsWith('/market-pricing') && !window.location.pathname.endsWith('/multi-job-pricing') && !window.location.pathname.endsWith('/propagate-matches') && !window.location.pathname.endsWith('/data-cuts')) {
      this.props.clearAllSelections();
      window.localStorage.removeItem('selectedJobs');
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  getSessionData(isRefresh = false) {
    this.props.getSession(isRefresh);
  }

  setAppData() {
    const { session } = this.props;

    //Note: GTM requires specific naming. Refer to Pendo confluence
    //https://confluence.underpaid.com/pages/viewpage.action?pageId=20519847
    //TODO consolidate graphql (mpay) data. masterAccountInfo set up for mpay
    session.isResellerAccount = false;
    try {
      if (session?.organization?.accountMetaData) {
        if (typeof session.organization.accountMetaData === 'string') {
          session.organization.accountMetaData = JSON.parse(session.organization.accountMetaData);
        }

        if (
          session.organization.accountMetaData.isResellerAccount === 'true' ||
          session.organization.accountMetaData.isResellerAccount === true
        ) {
          session.isResellerAccount = true;
        }
      }
    } catch (err) {
      console.error(err);
    }

    const splitTests = setPendoDarklyFlags(this.state.launchDarklyFeatureFlags);
    const transformedFlags = transformFeatureFlags(session.featureFlags);
    const featureFlag = setPendoFeatureFlags(transformedFlags);

    window.app = {
      super_admin: session.user.rights.superAdmin === true ? 1 : null,
      subsite: `${session.organization.code}`,
      userId: `${session.user.id}`,
      org_name: `${session.organization.name}`,
      organizationName: `${session.organization.name}`,
      masterAccountId: `${session.organization.masterAccountId}`,
      group: `${session.group.name}`,
      email: `${session.user.email}`,
      username: `${session.user.email}`,
      first_names: `${session.user.firstName}`,
      last_name: `${session.user.lastName}`,
      client_mgr_email: `${session.organization.clientManager}`,
      implementation_mgr_email: `${session.organization.implementationManager}`,
      accountId: `${session.organization.id}`,
      basicProductName: BASIC_PRODUCT_NAME,
      salesforceID: session.salesforceID,
      accountName: session.masterAccountServiceProductName
        ? session.masterAccountServiceProductName
        : `${session.organization.name}`,
      role: `${session.user.rights.primaryRole}`,
      isInternalUser: session.user.isInternalUser,
      version: session.codeReleaseVersion ? session.codeReleaseVersion : 'develop',
      isResellerAccount: session.isResellerAccount,
      splitTests: splitTests,
      featureFlag: featureFlag,
      monetary_display: session.user.preferences.monetaryDisplay,
      monetaryDivisor: session.user.preferences.monetaryDivisor,
      field_formatting: {
        numeric_rounding: session.user.preferences.numericRounding,
        monetary_rounding: session.user.preferences.monetaryRounding,
        percent_rounding: session.user.preferences.percentRounding
      },
      currencies: this.props.currency.currencies,
      csrf_token: session.csrfPair ? session.csrfPair.csrf_token : null,
      csrf_timestamp: session.csrfPair ? session.csrfPair.csrf_timestamp : null
    };
  }

  initLaunchDarklyFeatureFlags = () => {
    const { session } = this.props;
    const userId = session.user.id.toString();
    const user = {
      key: userId,
      firstName: session.user.firstName,
      lastName: session.user.lastName,
      email: session.user.email,
      custom: {
        subsite: session.organization.code,
        masterAccountId: session.organization.masterAccountId,
        organizationId: session.organization.id,
        organizationGroupId: session.group.id
      }
    };
    const isInProductionMode = isProduction();
    const clientId = isInProductionMode ? '5b0dcbd3b508e030b6460dd6' : '5b0dcbd3b508e030b6460dd5';
    const client = LDClient.initialize(clientId, user, { diagnosticOptOut: true });
    client.on('ready', () => {
      const flags = client.allFlags();
      this.setState({ launchDarklyFeatureFlags: flags, appLoadFinalProps: true });
    });
  };

  validateSession = () => {
    resetIdleTimer();
    this.props.validateSession();
  };

  onAction = () => {
    resetActionTimer();
  };

  renderRedirects() {
    const { featureFlags } = this.props.session;

    const { siteAdmin, superAdmin } = this.props.session.user.rights;

    const showLibraryUpdateFlag = featureFlags.find(x => (x.name = 'showLibraryUpdate'));
    const sluFeatureEnabled = showLibraryUpdateFlag?.enabled;
    const sluUserPermissionEnabled = siteAdmin || superAdmin;

    return [
      (!sluFeatureEnabled || !sluUserPermissionEnabled) && (
        <Route path="/iframe/merit/merit-admin/slu/slu">
          <Redirect to="/" />
        </Route>
      ),
      !superAdmin && (
        <Route path="/admin/users/superadmins">
          <Redirect to="/" />
        </Route>
      )
    ].filter(Comp => Comp);
  }

  render() {
    const { error, error502, loading, loggedIn, organization, user } = this.props.session;
    const { launchDarklyFeatureFlags, navCollapsed } = this.state;
    const glojamV2Enabled = launchDarklyFeatureFlags && launchDarklyFeatureFlags['glojam-v2']
    const accountMetaData = organization?.accountMetaData;

    if (loading) {
      return (
        <div className="app__container">
          <InPlaceIndicator />
        </div>
      );
    }

    const loginDomain = this.props.config.openACSHost;
    this.loginUrl = `${loginDomain}/oauth/${this.props.config.orgCode}/login`;
    this.homepageURL = `/view/o/${this.props.config.orgCode}/mp/`;

    if (error && loggedIn) {
      return (
        <StatusError message="Sorry, something went wrong!" url={this.homepageURL} onClick={this.props.resetError} />
      );
    }

    if (error502) {
      return (
        <StatusError message="Sorry, something went wrong! We're investigating. In the meantime, please try again." />
      );
    }

    if (!loggedIn) {
      return <SessionError message="Sorry, your session has expired due to inactivity." loginUrl={this.loginUrl} />;
    }

    this.setAppData();
    if (!this.props.session?.user?.rights) return null;

    const {
      globalAdmin,
      groupAdmin,
      readOnlyUser,
      regularUser,
      reportViewUser,
      restrictedUser,
      siteAdmin,
      superAdmin
    } = this.props.session.user.rights;
    const isAdmin = globalAdmin || groupAdmin || siteAdmin || superAdmin;
    const isInSiteGroup = this.props.session.group.id === this.props.session.organization.defaultGroup.id;
    const showJDXpert =
      this.props.session.organization?.accountMetaData?.enableHRTMSIntegration && !readOnlyUser && !restrictedUser && !reportViewUser;

    const isReportViewer = reportViewUser;
    const defaultLayoutProps = {
      session: this.props.session,
      featureFlags: this.props.session.featureFlags,
      config: this.props.config,
      launchDarklyFeatureFlags: launchDarklyFeatureFlags,
      loginUrl: this.loginUrl,
      navCollapsed: navCollapsed,
      toggleCollapse: this.toggleCollapse,
      history: this.props.history,
      appLoadFinalProps: this.state.appLoadFinalProps,
      showSlideout: this.props.showSlideout
    };

    let { location } = this.props;
    const useJobCodeKey = this.props.session.organization.useJobCodeKey;
    const usePropagateMatches = this.props.session.featureFlags.find(flag=> flag.name === 'usePropagateMatches').enabled;

    let isModal = !!(location.state?.modal && this.previousLocation !== location);
    const hasModifier = location.pathname.includes('/delete') || location.pathname.includes('settings');
    const bodyTag = document.getElementsByTagName('body')[0];
    bodyTag.classList.remove('is-error-page');

    return (
      <MantineProvider theme={theme}>
        <DndProvider backend={HTML5Backend}>
          <React.Fragment>
            <IdleTimeWatcher
              enabled={launchDarklyFeatureFlags['session-idle-timer']}
            />
            <Switch
              onChange={this.validateSession}
              location={isModal && hasModifier ? location : isModal ? this.previousLocation : location}
            >
              {/* <DefaultLayoutRoute
                path={DATA_ROUTE}
                exact
                component={Data}
                layoutProps={defaultLayoutProps}
                componentProps={{
                  app: app,
                  isEnabled: launchDarklyFeatureFlags['dyson'],
                  onUnauthorized: () => {
                    window.location = `/${this.props.config.orgCode}/app/auth/logout`;
                  }
                }}
              />
              <DefaultLayoutRoute
                path={'/upload'}
                component={Upload}
                layoutProps={defaultLayoutProps}
                componentProps={{
                  app: app,
                  isEnabled: launchDarklyFeatureFlags['dyson'],
                  useFullPageLayout: true,
                  onUnauthorized: () => {
                    window.location = `/${this.props.config.orgCode}/app/auth/logout`;
                  },
                  hrisGroup: hrisGroup,
                  jobDescriptionsGroup: jobDescriptionsGroup,
                  surveyGroup: surveyGroup,
                  otherGroup: otherGroup
                }}
              /> */}
              {this.renderRedirects()}
              <PrintLayoutRoute path="/jobs/:id/:code?/print" component={CompensationSummaryPdf} />
              <PrintLayoutRoute path="/models/:modelId/:basis/print" component={StructureModelingAnalyticsSlideout} />
              <PrintLayoutRoute path="/yearoveryear/:orgJobId/print" component={YearOverYearContainer} />
              {!restrictedUser && !isReportViewer && (
                <DefaultLayoutRoute
                  path="/market-analysis/jobs/:jobId/:tab"
                  component={MarketPricing}
                  layoutProps={defaultLayoutProps}
                  componentProps={{ launchDarklyFeatureFlags }}
                />
              )}
              <Route
                path="/oauth/:orgCode"
                render={() => {
                  window.location = this.loginUrl;
                }}
              />
              <DefaultLayoutRoute
                path="/reports/rls"
                exact
                component={ReportLibraryNew}
                componentProps={{
                  enabled: launchDarklyFeatureFlags['report-library-mfe']
                }}
                layoutProps={defaultLayoutProps}
              />
              <DefaultLayoutRoute
                path="/reports/library"
                exact
                component={ReportLibrary}
                layoutProps={defaultLayoutProps}
                componentProps={{ launchDarklyFeatureFlags }}
              />
              <DefaultLayoutRoute
                path="/reports/:reportPk"
                exact
                component={Report}
                layoutProps={defaultLayoutProps}
                componentProps={{ launchDarklyFeatureFlags }}
              />
              <DefaultLayoutRoute
                exact
                path="/landing-page"
                component={LandingDashboardPage}
                componentProps={{
                  launchDarklyFeatureFlags,
                  readOnlyUser,
                  restrictedUser
                }}
                layoutProps={defaultLayoutProps}
              />
              {showJDXpert && (
                <DefaultLayoutRoute exact path="/jdxpert" component={JDXIframe} layoutProps={defaultLayoutProps} />
              )}
              {!reportViewUser && (
                <DefaultLayoutRoute
                  exact
                  path="/market-analysis/market-pricing"
                  component={LandingPage}
                  componentProps={{
                    launchDarklyFeatureFlags,
                    readOnlyUser,
                    restrictedUser,
                    useJobCodeKey,
                    usePropagateMatches
                  }}
                  layoutProps={defaultLayoutProps}
                />
              )}
              {!readOnlyUser && isInSiteGroup && glojamV2Enabled && (
                <DefaultLayoutRoute
                  exact
                  path="/market-analysis/strategy-based-pricing"
                  component={GlobalJobMatchingVersionsList}
                  componentProps={{
                    launchDarklyFeatureFlags
                  }}
                  layoutProps={defaultLayoutProps}
                />
              )}
              {!readOnlyUser && isInSiteGroup && (
                <DefaultLayoutRoute
                  exact
                  path="/market-analysis/strategy-based-pricing/:id"
                  component={GlobalJobMatchingPricingVersionStepper}
                  componentProps={{
                    launchDarklyFeatureFlags
                  }}
                  layoutProps={defaultLayoutProps}
                />
              )}
              {!readOnlyUser && isInSiteGroup && !glojamV2Enabled && (
                <DefaultLayoutRoute
                  exact
                  path="/market-analysis/global-job-matching/:id"
                  component={EditPricingVersionDetailsPage}
                  componentProps={{
                    launchDarklyFeatureFlags
                  }}
                  layoutProps={defaultLayoutProps}
                />
              )}
              {launchDarklyFeatureFlags['global-job-matching'] && !readOnlyUser && (
                <DefaultLayoutRoute
                  path="/market-analysis/jobs/:jobId"
                  component={GlobalJobMatching}
                  layoutProps={defaultLayoutProps}
                />
              )}
              {!readOnlyUser && isInSiteGroup && !glojamV2Enabled && (
                <DefaultLayoutRoute
                  path="/market-analysis/global-job-matching"
                  component={GlobalJobPricingVersioningLanding}
                  layoutProps={defaultLayoutProps}
                />
              )}
              {launchDarklyFeatureFlags['global-job-matching'] && !readOnlyUser && (
                <DefaultLayoutRoute
                  path="/market-analysis/jobs"
                  component={GlobalJobMatching}
                  layoutProps={defaultLayoutProps}
                />
              )}
              <DefaultLayoutRoute
                path="/market-analysis/survey-library/:tab"
                component={SurveyLibraryUpdate}
                layoutProps={defaultLayoutProps}
              />
              <DefaultLayoutRoute
                exact
                path="/market-analysis/job-matching/"
                component={JobMatching}
                layoutProps={defaultLayoutProps}
              />
              <DefaultLayoutRoute
                path="/market-analysis/rules-based-pricing/rules/:ruleId"
                component={RulesBasedPricing}
                layoutProps={defaultLayoutProps}
              />
              <DefaultLayoutRoute
                path="/market-analysis/rules-based-pricing"
                component={RulesBasedPricing}
                layoutProps={defaultLayoutProps}
              />
              <DefaultLayoutRoute
                path="/market-analysis/data-cut-migration"
                component={DataCutMigrationPage}
                layoutProps={defaultLayoutProps}
                exact
              />
              <DefaultLayoutRoute
                path="/market-analysis/data-cut-migration/:migrationId"
                component={DataCutMigrationDetailsPage}
                layoutProps={defaultLayoutProps}
              />
              <DefaultLayoutRoute
                path="/market-analysis/differentials-engine"
                component={PayScalePulsePage}
                componentProps={{
                  geoUrl: '/market-analysis/geo-differential',
                  skillsUrl: '/market-analysis/differentials-engine',
                  selectedCurrencyCode: this.props.session.currency.code,
                  readOnlyUser:
                    readOnlyUser ||
                    (!isAdmin &&
                      regularUser &&
                      !(accountMetaData?.allowRegUserApplyGeo == null ? true : accountMetaData?.allowRegUserApplyGeo))
                }}
                layoutProps={defaultLayoutProps}
              />
              <DefaultLayoutRoute
                path="/market-analysis/geo-differential"
                component={GeoDifferentialPage}
                componentProps={{
                  isCompanyLocationTabEnabled: true,
                  isPayScalePulseEnabled: launchDarklyFeatureFlags['payscale-pulse'],
                  landingPageURL: '/market-analysis/differentials-engine',
                  showInternalDataSetting: true,
                  readOnlyUser:
                    readOnlyUser ||
                    (!isAdmin &&
                      regularUser &&
                      !(accountMetaData?.allowRegUserApplyGeo == null ? true : accountMetaData?.allowRegUserApplyGeo))
                }}
                layoutProps={defaultLayoutProps}
              />
              {!readOnlyUser && (
                <DefaultLayoutRoute
                  path="/market-analysis/multi-job-pricing"
                  component={BulkPricing}
                  layoutProps={defaultLayoutProps}
                  componentProps={{ 
                    usePropagateMatches,
                    useJobCodeKey 
                  }}
                />
              )}
              {usePropagateMatches && useJobCodeKey && !readOnlyUser && !reportViewUser && !restrictedUser && (
                <DefaultLayoutRoute
                  path="/market-analysis/propagate-matches"
                  component={PropagateMatches}
                  layoutProps={defaultLayoutProps}
                />
              )}
              <Route
                path={IFRAME_PATH}
                render={() => (
                  <MeritIframeRoutes
                    componentProps={{
                      launchDarklyFeatureFlags,
                      validateSession: this.validateSession,
                      onAction: this.onAction.bind(this),
                      getSessionData: this.getSessionData
                    }}
                    location={location}
                    layoutProps={defaultLayoutProps}
                  />
                )}
              />
              <DefaultLayoutRoute
                path="/crowdsource-jobs"
                component={AddJobWrapper}
                layoutProps={defaultLayoutProps}
                componentProps={{ launchDarklyFeatureFlags, validateSession: this.validateSession }}
              />
              <DefaultLayoutRoute path="/ranges" component={JobBasedRanges} layoutProps={defaultLayoutProps} />
              <DefaultLayoutRoute path="/sftp-setup" component={SFTPSetup} layoutProps={defaultLayoutProps} />
              <DefaultLayoutRoute
                path="/structure-modeling"
                component={StructureModelingPage}
                componentProps={{ session: this.props.session }}
                layoutProps={defaultLayoutProps}
              />
              <DefaultLayoutRoute
                path="/admin/marketpay-internal"
                component={SuperAdmin}
                layoutProps={defaultLayoutProps}
              />
              <DefaultLayoutRoute
                path="/"
                component={Home}
                componentProps={{ launchDarklyFeatureFlags }}
                layoutProps={defaultLayoutProps}
              />
              <DefaultLayoutRoute component={NoMatch} layoutProps={defaultLayoutProps} />
            </Switch>
            {isModal ? (
              <ModalLayoutRoute
                layoutProps={defaultLayoutProps}
                componentProps={{
                  modalType: this.props.location.state.modalType,
                  modalProps: this.props.location.state.modalProps
                }}
              />
            ) : null}

            <AlertToastContainer className="alert-toast-container" position="top-center" />
          </React.Fragment>
        </DndProvider>
      </MantineProvider>
    );
  }
}

const mapStateToProps = state => {
  const { config, session, group, currency, reports } = state;
  return {
    config,
    session,
    group,
    currency,
    reports
  };
};

export default withRouter(
  connect(mapStateToProps, {
    getSession,
    validateSession,
    resetError,
    showSlideout,
    clearReportFilters,
    clearAllSelections,
    clearEmployeeFilter
  })(App)
);
